import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, Search, AlertTriangle, Clock, FileWarning, UserX, CheckCircle2, Users } from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../lib/auth';
import { Input } from '../../components/ui/input';
import { OverviewTable } from './OverviewTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { normalizeTr, STATUS_TABS, ISSUE_FILTERS, matchesIssue, daysText } from './utils';

function Kpi({ icon: Icon, label, value, tone, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} className={`text-left rounded-xl border p-3 transition-colors ${active ? 'ring-2 ring-sky-500 border-sky-300' : 'border-slate-200 dark:border-slate-700 hover:border-slate-400'} bg-white dark:bg-slate-900`} data-testid={testId}>
      <div className="flex items-center justify-between text-xs text-slate-500"><span>{label}</span><Icon className={`h-4 w-4 ${tone}`} /></div>
      <div className="text-2xl font-bold text-slate-900 dark:text-white mt-0.5">{value}</div>
    </button>
  );
}

export default function EmployeeOverview() {
  const { user } = useAuthStore();
  const [status, setStatus] = useState('active');
  const [issue, setIssue] = useState('all');
  const [search, setSearch] = useState('');
  const [firmId, setFirmId] = useState('');
  const [firms, setFirms] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const isSuper = user?.role === 'SUPER_ADMIN';

  useEffect(() => { if (isSuper) api.get('/firms/').then((r) => setFirms(r.data?.data || [])).catch(() => {}); }, [isSuper]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/employee-profile/overview', { params: { status, ...(firmId ? { firm_id: firmId } : {}) } });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [status, firmId]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = normalizeTr(search.trim());
    return (data?.rows || []).filter((r) => matchesIssue(r, issue) && (!q || normalizeTr(r.full_name || '').includes(q) || r.vehicles.some((v) => normalizeTr(v.plate || '').includes(q))));
  }, [data, issue, search]);

  const allAlerts = useMemo(() => (data?.rows || []).filter((r) => r.expiry_alert), [data]);
  const alerts = allAlerts.slice(0, 8);
  const s = data?.summary;
  const toggle = (id, forceOpen = false) => setExpanded((e) => (e === id && !forceOpen ? null : id));

  return (
    <div className="space-y-5" data-testid="employee-overview-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><ClipboardCheck className="h-7 w-7 text-sky-600" />Çalışan Evrak Özeti</h1>
        <p className="text-sm text-slate-500 mt-1">Eksik bilgi ve belgeler, kimlik / ehliyet / sürücü belgesi bitiş tarihleri ve araçlar tek ekranda</p>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi testId="overview-kpi-total" icon={Users} tone="text-sky-500" label="Çalışan" value={s.total} active={issue === 'all'} onClick={() => setIssue('all')} />
          <Kpi testId="overview-kpi-expired" icon={AlertTriangle} tone="text-red-500" label="Süresi dolan" value={s.expired} active={issue === 'expired'} onClick={() => setIssue('expired')} />
          <Kpi testId="overview-kpi-soon" icon={Clock} tone="text-orange-500" label="30 gün içinde" value={s.soon} active={issue === 'soon'} onClick={() => setIssue('soon')} />
          <Kpi testId="overview-kpi-missing-docs" icon={FileWarning} tone="text-rose-500" label="Eksik belge" value={s.missing_docs} active={issue === 'missing_docs'} onClick={() => setIssue('missing_docs')} />
          <Kpi testId="overview-kpi-missing-info" icon={UserX} tone="text-amber-500" label="Eksik bilgi" value={s.missing_info} active={issue === 'missing_info'} onClick={() => setIssue('missing_info')} />
          <Kpi testId="overview-kpi-complete" icon={CheckCircle2} tone="text-emerald-500" label="%100 tamam" value={s.complete} active={issue === 'complete'} onClick={() => setIssue('complete')} />
        </div>
      )}

      {alerts.length > 0 && (
        <div className="rounded-xl border-2 border-red-300 bg-red-50/70 dark:bg-red-950/30 dark:border-red-800 p-3" data-testid="overview-expiry-alerts">
          <div className="flex items-center gap-2 text-sm font-bold text-red-700 dark:text-red-300 mb-2"><AlertTriangle className="h-4 w-4" />Belge süresi dolan / dolmak üzere olanlar</div>
          <div className="flex flex-wrap gap-2">
            {alerts.map((r) => {
              const bad = Object.entries(r.documents).filter(([, d]) => d.state === 'expired' || d.state === 'soon');
              return (
                <div key={r.user_id} className="flex items-center gap-2 rounded-lg bg-white dark:bg-slate-900 border border-red-200 dark:border-red-900 px-2.5 py-1.5">
                  <span className="text-sm font-semibold">{r.full_name}</span>
                  <span className="text-[11px] text-red-600">{bad.map(([k, d]) => `${{ identity: 'Kimlik', license: 'Ehliyet', attest: 'Sürücü b.' }[k]}: ${daysText(d.days)}`).join(' · ')}</span>
                  <button type="button" onClick={() => { setIssue('all'); setSearch(''); toggle(r.user_id, true); setTimeout(() => document.querySelector(`[data-testid="overview-row-${r.user_id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); }} className="text-[11px] font-bold rounded bg-red-600 text-white px-2 py-0.5 hover:bg-red-700" data-testid={`overview-alert-update-${r.user_id}`}>
                    Güncelle
                  </button>
                </div>
              );
            })}
            {allAlerts.length > alerts.length && (
              <button type="button" onClick={() => setIssue('expired')} className="text-xs font-semibold text-red-700 underline" data-testid="overview-alerts-more">
                +{allAlerts.length - alerts.length} kişi daha
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-white dark:bg-slate-900">
          {STATUS_TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => { setStatus(t.key); setExpanded(null); }} className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${status === t.key ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`} data-testid={`overview-status-${t.key}`}>
              {t.label} <span className="opacity-60">{data?.status_counts?.[t.key] ?? ''}</span>
            </button>
          ))}
        </div>
        <Select value={issue} onValueChange={setIssue}>
          <SelectTrigger className="h-8 w-[200px]" data-testid="overview-issue-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{ISSUE_FILTERS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
        {isSuper && (
          <Select value={firmId || 'all'} onValueChange={(v) => setFirmId(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 w-[180px]" data-testid="overview-firm-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm firmalar</SelectItem>
              {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <div className="relative ml-auto">
          <Search className="h-4 w-4 absolute left-2.5 top-2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İsim veya plaka ara…" className="h-8 pl-8 w-64" data-testid="overview-search" />
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      <OverviewTable rows={rows} expanded={expanded} onToggle={toggle} onSaved={load} />
    </div>
  );
}
